/**
 * LockedSectionPopup — попап який з'являється коли FREE юзер тапає на
 * заблокований розділ (КБ / Курс / Профіль).
 *
 * Per владелец 2026-05-20: «нажимаю на раздел с замочком → показываем
 * человеку небольшой поп-ап, где пишем что это за раздел, какая ценность
 * и что доступ можно получить с подпиской».
 *
 * Соответствует stylevу платформы (Mini App), не Telegram nativeAlert.
 */

import { createPortal } from "react-dom";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { popupActionsCol, popupBtnGhost, popupBtnPrimary } from "@/lib/popupButtons";

export interface LockedSection {
  emoji: string;
  title: string;
  description: string;
  benefits: string[];
}

const SECTIONS: Record<string, LockedSection> = {
  knowledge_base: {
    emoji: "📚",
    title: "База знань",
    description:
      "Підкасти, статті та десятки уроків від засновника inFLOW і запрошених спікерів — про продажі, мислення, гроші, бізнес і масштабування.",
    benefits: [
      "20+ подкастів від засновника inFLOW — продажі, мислення, психологія, гроші, бізнес",
      "20+ статей від засновника — фріланс, масштабування, заробіток",
      "30+ уроків від десятка спікерів — медійні блогери та експерти в кожній ніші",
      "Регулярні оновлення новим контентом",
    ],
  },
  course: {
    emoji: "🎬",
    title: "Курс від засновника",
    description:
      "6 уроків + записаний ефір про легкі продажі, ціну та пошук клієнтів — від засновника inFLOW з 10+ роками досвіду.",
    benefits: [
      "Як вийти на 1000$",
      "Чому в тебе не купують",
      "Що впливає на к-сть клієнтів",
      "Філософія легких продажів",
      "Працюй без конкуренції",
      "Якщо клієнтам дорого",
      "11 способів пошуку клієнтів (ефір)",
    ],
  },
  saved: {
    emoji: "🔖",
    title: "Збережені вакансії",
    description:
      "Місце для вакансій, які ти відклав, щоб повернутись і написати замовнику, коли буде час.",
    benefits: [
      "Кнопка «Зберегти» під кожною вакансією в боті і в її картці",
      "Збережене лежить окремим списком — не губиться у стрічці",
      "Виглядає і відкривається так само, як стрічка вакансій",
      "Прибрати зі списку можна тією ж кнопкою",
    ],
  },
  profile: {
    emoji: "👤",
    title: "Профіль",
    description:
      "Налаштування твого профілю — bio для AI, категорії вакансій, портфоліо.",
    benefits: [
      "AI генерує відгуки на основі твого профілю",
      "Точкове налаштування категорій вакансій",
      "Портфоліо та контакти для замовників",
      "Управління підпискою та автопродовженням",
    ],
  },
};

export function LockedSectionPopup({
  sectionKey,
  open,
  onClose,
  isTest = false,
}: {
  sectionKey: keyof typeof SECTIONS | null;
  open: boolean;
  onClose: () => void;
  /** true → юзер на тест-підписці: розділ недоступний саме на тесті
   *  (а не «потрібна підписка»). Юзер 2026-06-11. */
  isTest?: boolean;
}) {
  // Лочимо body scroll коли popup відкритий + блокуємо bounce iOS
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !sectionKey) return null;
  const section = SECTIONS[sectionKey];
  if (!section) return null;

  // Render через React Portal у document.body — щоб не обрізалось
  // батьківським Layout (overflow-x-hidden / max-w-md / etc.) і
  // position:fixed працював відносно viewport, а не контейнера.
  // CENTER alignment + max-h:85vh + scroll — щоб контент завжди вміщався
  // навіть якщо Telegram WebApp viewport маленький (split screen, keyboard).
  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // Використовуємо Telegram viewport якщо є, інакше 100vh
        height:
          typeof window !== "undefined" &&
          (window as any).Telegram?.WebApp?.viewportStableHeight
            ? `${(window as any).Telegram.WebApp.viewportStableHeight}px`
            : "100vh",
      }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-surface rounded-3xl p-5 shadow-2xl overflow-y-auto"
        style={{ maxHeight: "calc(100% - 32px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-4">
          <div className="mb-2 flex justify-center">
            <TgEmoji char={section.emoji} size={44} />
          </div>
          <div className="font-display text-[1.125rem] font-bold text-ink leading-tight">
            {section.title}
          </div>
          <div className="text-[0.78125rem] text-ink-dim mt-1.5 leading-relaxed [text-wrap:balance]">
            {section.description}
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 p-3.5 mb-4">
          <div className="font-display text-[0.6875rem] font-bold text-ink mb-2 uppercase tracking-wide flex items-center gap-2">
            <span className="flex w-4 shrink-0 justify-center">
              <TgEmoji char="🎁" size={16} />
            </span>
            Що отримаєш:
          </div>
          <ul className="space-y-1 text-[0.75rem] text-ink/85">
            {section.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2 leading-snug">
                <span className="flex w-4 shrink-0 justify-center text-primary">✓</span>
                <span className="flex-1">{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl bg-ink/[0.04] border border-ink/10 p-2.5 mb-4 flex items-center gap-2">
          <TgEmoji char="💰" size={18} />
          <div className="text-[0.71875rem] text-ink-dim leading-snug flex-1">
            {isTest
              ? "Доступ до цього розділу недоступний на тестовій підписці."
              : "Доступ до цього розділу — лише з активною підпискою."}
          </div>
        </div>

        <div className={popupActionsCol}>
          <Link to="/subscription" onClick={onClose} className={popupBtnPrimary}>
            Обрати підписку
          </Link>
          <button type="button" onClick={onClose} className={popupBtnGhost}>
            Пізніше
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
